import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { Stack, Typography } from '../components/mui-system';
import { api } from '../api/client';
import {
  PROBLEM_LABELS,
  type DonorBulkInput,
  type DonorBulkPreview,
  type DonorBulkResult,
  type DonorProblemKind,
} from '../../../api/src/donors/donors.types';

export const DONOR_ACTION_LABELS = {
  archive: 'Архивировать',
  restore: 'Восстановить',
  recheck: 'Перепроверить',
  delete: 'Удалить навсегда',
};
export function DonorBulkDialog({
  workspace,
  input,
  onClose,
  onDone,
}: {
  workspace: string;
  input: DonorBulkInput;
  onClose: () => void;
  onDone: (result: DonorBulkResult) => void;
}) {
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<DonorBulkPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempted, setAttempted] = useState(false);
  async function prepare() {
    setBusy(true);
    setError('');
    try {
      setPreview(
        await api.previewDonorBulk(workspace, {
          ...input,
          reason: reason.trim() || DONOR_ACTION_LABELS[input.action],
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function execute() {
    setBusy(true);
    setError('');
    setAttempted(true);
    try {
      onDone(await api.executeDonorBulk(workspace, preview!.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={() => !busy && onClose()}>
      <DialogTitle>{DONOR_ACTION_LABELS[input.action]}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Typography>
            {input.ids
              ? `Выбрано вручную: ${input.ids.length}.`
              : 'Выбраны все статьи, подходящие под текущие фильтры, со всех страниц.'}
          </Typography>
          {input.action === 'delete' ? (
            <Alert severity="warning">
              Статьи, ожидаемые ссылки и история их проверок будут удалены без возможности
              восстановления. Журнал действий и ранее подготовленные отчёты сохранятся. Повторный
              импорт сможет создать эти статьи заново.
            </Alert>
          ) : (
            <Alert severity="info">
              {input.action === 'archive'
                ? 'Проверки выбранных статей остановятся. Данные и доступная история сохранятся в архиве.'
                : input.action === 'restore'
                  ? 'Статьи вернутся в активный список с полной перепроверкой.'
                  : 'Полная перепроверка пройдёт с обычными ограничениями нагрузки.'}
            </Alert>
          )}
          <TextField
            label="Причина / комментарий"
            value={reason}
            disabled={busy || attempted}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
            onChange={(event) => {
              setReason(event.target.value);
              setPreview(null);
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {preview && (
            <>
              <Typography fontWeight={700}>Будет обработано: {preview.total}</Typography>
              {Object.entries(preview.reasons).map(([key, count]) => (
                <Typography key={key}>
                  {PROBLEM_LABELS[key as DonorProblemKind] ??
                    {
                      found: 'Ссылки найдены',
                      changed: 'Ссылки изменились',
                      unchecked: 'Не проверено',
                      missing: 'Ссылки не найдены',
                      unavailable: 'Недоступно',
                      unknown: 'Не удалось проверить',
                    }[key] ??
                    key}
                  : {count}
                </Typography>
              ))}
              {preview.sample.map((item) => (
                <Typography key={item.url} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {item.url}
                  {item.problem.since
                    ? ` · с ${new Date(item.problem.since).toLocaleDateString('ru-RU')}; подтверждений: ${item.problem.confirmations}`
                    : ''}
                </Typography>
              ))}
              <Typography variant="body2">
                Изменившиеся после предпросмотра записи будут пропущены. Предпросмотр действует 15
                минут.
              </Typography>
            </>
          )}
          {busy && <Typography>Обрабатываем порциями по 100 статей…</Typography>}
          {attempted && error && (
            <Alert severity="info">
              Часть операции могла завершиться. Повторное нажатие продолжит ту же операцию без
              повторной обработки.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Закрыть
        </Button>
        {!attempted && (
          <Button disabled={busy} onClick={() => void prepare()}>
            Предпросмотр
          </Button>
        )}
        <Button
          variant="contained"
          color={input.action === 'delete' ? 'error' : 'primary'}
          disabled={busy || !preview?.total}
          onClick={() => void execute()}
        >
          {attempted && error ? 'Продолжить операцию' : DONOR_ACTION_LABELS[input.action]}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
